// src/utils/searchHelper.js

/**
 * Normaliza un texto: convierte a minúsculas, remueve acentos/diacríticos,
 * signos de puntuación y espacios extras.
 */
export const normalizar = (str = '') => {
  if (!str || typeof str !== 'string') return '';
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

/**
 * Diccionario de equivalencias, sinónimos y términos en inglés / español.
 */
export const SINONIMOS = {
  // Colágeno
  'collagen': ['colageno', 'colagen'],
  'colagen': ['colageno', 'collagen'],
  'colageno': ['collagen', 'colagen'],
  'colajeno': ['colageno', 'collagen'],

  // Proteínas
  'protein': ['proteina', 'whey'],
  'proteina': ['protein', 'whey'],
  'proteinas': ['proteina', 'protein'],
  'whey': ['proteina', 'protein'],

  // Magnesio
  'magnesium': ['magnesio', 'citrato'],
  'magnesio': ['magnesium', 'citrato'],
  'magnecio': ['magnesio', 'magnesium'],

  // Vitaminas
  'vitamin': ['vitamina'],
  'vitamina': ['vitamin'],
  'vit': ['vitamina'],

  // Omega
  'omega': ['omega 3', 'pescado', 'fish oil'],
  'omeag': ['omega', 'omega 3'],
  'fish': ['pescado', 'omega'],

  // Salud hepática / Detox
  'liver': ['higado', 'hepatico', 'detox'],
  'higado': ['liver', 'hepatico', 'detox'],
  'hepatico': ['higado', 'liver', 'detox'],
  'detox': ['higado', 'hepatico', 'reset', 'desintoxicar'],

  // Sueño / Descanso
  'sleep': ['sueno', 'dormir', 'descanso', 'melatonina'],
  'sueno': ['sleep', 'dormir', 'descanso', 'melatonina'],
  'dormir': ['sleep', 'sueno', 'descanso'],
  'descanso': ['sueno', 'dormir', 'sleep'],

  // Estrés / Calma
  'stress': ['estres', 'calma', 'relajacion'],
  'estres': ['stress', 'calma', 'relajacion'],

  // Articulaciones / Dolor
  'joint': ['articulaciones', 'articular', 'dolor', 'movilidad'],
  'articular': ['articulaciones', 'joint', 'dolor'],
  'articulacion': ['articular', 'articulaciones', 'joint'],

  // Calcio / Huesos
  'calcium': ['calcio', 'huesos'],
  'calcio': ['calcium', 'huesos'],
  'bones': ['huesos', 'calcio'],
  'huesos': ['calcio', 'bones'],

  // Peso / Adelgazar
  'weight': ['peso', 'quemador', 'adelgazar'],
  'peso': ['weight', 'adelgazar', 'quemador'],
  'adelgazar': ['peso', 'quemador', 'weight'],

  // Perfiles de usuario (específicos y exactos)
  'kids': ['ninos', 'infantil'],
  'children': ['ninos', 'infantil'],
  'ninos': ['nino', 'infantil', 'kids'],
  'nino': ['ninos', 'infantil', 'kids'],
  'infantil': ['ninos', 'kids'],

  'women': ['mujer', 'mujeres', 'femenino'],
  'mujer': ['mujeres', 'femenino', 'femenina'],
  'mujeres': ['mujer', 'femenino'],
  'femenino': ['mujer', 'mujeres'],

  'men': ['hombre', 'hombres', 'masculino'],
  'hombre': ['hombres', 'masculino'],
  'hombres': ['hombre', 'masculino'],
  'masculino': ['hombre', 'hombres'],

  'senior': ['adultos mayores', 'abuelo', 'tercera edad'],
  'adultos mayores': ['adulto mayor', 'abuelo', 'tercera edad', 'senior'],
  'adulto mayor': ['adultos mayores', 'abuelo', 'tercera edad'],
  'anciano': ['adultos mayores', 'abuelo'],
  'abuelo': ['adultos mayores', 'abuelos'],
  'abuelos': ['adultos mayores', 'abuelo'],
};

/**
 * Distancia de Levenshtein para medir similitud tipográfica entre dos palabras.
 */
export const distanciaLevenshtein = (a, b) => {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  const matrix = [];
  for (let i = 0; i <= b.length; i++) matrix[i] = [i];
  for (let j = 0; j <= a.length; j++) matrix[0][j] = j;

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }
  return matrix[b.length][a.length];
};

/**
 * Evita confusiones tipográficas entre términos biológicos o médicos diferentes.
 */
const esFalsoAmigo = (a, b) => {
  // Hombro (articulación) vs Hombre (género)
  if ((a.startsWith('hombro') && b.startsWith('hombre')) || (a.startsWith('hombre') && b.startsWith('hombro'))) {
    return true;
  }
  // Riñón (órgano/renal) vs Niño (infantil)
  if (((a.startsWith('rinon') || a.startsWith('renal')) && (b.startsWith('nino') || b.startsWith('nina'))) ||
      ((b.startsWith('rinon') || b.startsWith('renal')) && (a.startsWith('nino') || a.startsWith('nina')))) {
    return true;
  }
  // Mente vs Men
  if ((a === 'men' && b.startsWith('ment')) || (b === 'men' && a.startsWith('ment'))) {
    return true;
  }
  return false;
};

/**
 * Determina si dos palabras son similares evitando falsos positivos de subcadenas cortas.
 */
export const sonPalabrasSimilares = (wQuery, wTarget) => {
  if (wQuery === wTarget) return true;
  if (!wQuery || !wTarget) return false;

  // Evitar falsos amigos médicos/biológicos
  if (esFalsoAmigo(wQuery, wTarget)) return false;

  // "men" en inglés es corto y no debe matchear palabras que lo contienen en español (suplemento, rendimiento, mente, etc.)
  if (wQuery === 'men' || wTarget === 'men') {
    return wQuery === wTarget;
  }

  // Si la palabra objetivo empieza con la consulta (ej. colagen -> colageno)
  if (wQuery.length >= 4 && wTarget.startsWith(wQuery)) return true;

  // Subcadena solo permitida para palabras de al menos 5 letras
  if (wQuery.length >= 5 && wTarget.includes(wQuery)) return true;

  const maxLen = Math.max(wQuery.length, wTarget.length);
  // Para palabras muy cortas (<= 3 letras), solo coincidencia exacta
  if (maxLen <= 3) return wQuery === wTarget;

  const dist = distanciaLevenshtein(wQuery, wTarget);
  if (maxLen <= 5) return dist <= 1;
  return dist <= 2;
};

/**
 * Expande las palabras clave del usuario incluyendo sus sinónimos.
 */
export const expandirPalabras = (queryTexto) => {
  const norm = normalizar(queryTexto);
  if (!norm) return [];
  const palabras = norm.split(' ').filter(Boolean);
  const expandidas = new Set(palabras);

  if (SINONIMOS[norm]) {
    SINONIMOS[norm].forEach(s => expandidas.add(s));
  }

  palabras.forEach(pal => {
    if (SINONIMOS[pal]) {
      SINONIMOS[pal].forEach(s => expandidas.add(s));
    }
  });

  return Array.from(expandidas);
};

/* ── Mapeo y Detección de Perfiles de Usuario ──────────────── */

const PERFIL_NINOS_KEYWORDS = ['ninos', 'nino', 'nina', 'ninas', 'kids', 'infantil', 'infantiles', 'children', 'pediatrico'];
const PERFIL_HOMBRE_KEYWORDS = ['hombre', 'hombres', 'masculino', 'masculina', 'men', 'caballero', 'caballeros', 'varon', 'varones'];
const PERFIL_MUJER_KEYWORDS = ['mujer', 'mujeres', 'femenino', 'femenina', 'women', 'dama', 'damas', 'mamas', 'mama', 'senora', 'chica'];
const PERFIL_MAYORES_KEYWORDS = ['adultos mayores', 'adulto mayor', 'abuelo', 'abuela', 'abuelos', 'abuelas', 'anciano', 'ancianos', 'tercera edad', 'senior', 'seniors', 'geriatrico'];

/**
 * Detecta si una cadena corresponde exclusivamente a un perfil de usuario.
 */
export const detectarPerfilUsuario = (queryTexto = '') => {
  const norm = normalizar(queryTexto);
  if (!norm) return null;

  if (PERFIL_NINOS_KEYWORDS.includes(norm)) return 'ninos';
  if (PERFIL_HOMBRE_KEYWORDS.includes(norm)) return 'hombre';
  if (PERFIL_MUJER_KEYWORDS.includes(norm)) return 'mujer';
  if (PERFIL_MAYORES_KEYWORDS.includes(norm)) return 'adultos_mayores';

  return null;
};

/**
 * Detecta si el query incluye mención de un perfil y extrae el resto de la búsqueda.
 * Ej: "colageno mujer" -> { perfil: 'mujer', resto: 'colageno' }
 */
export const extraerPerfilYTermino = (queryTexto = '') => {
  const norm = normalizar(queryTexto);
  if (!norm) return { perfil: null, resto: '' };

  const palabras = norm.split(' ');
  let perfilDetectado = null;
  const palabrasRestantes = [];

  for (let i = 0; i < palabras.length; i++) {
    const p = palabras[i];
    const dosPalabras = i < palabras.length - 1 ? `${p} ${palabras[i + 1]}` : null;

    if (dosPalabras && PERFIL_MAYORES_KEYWORDS.includes(dosPalabras)) {
      perfilDetectado = 'adultos_mayores';
      i++; // saltar la siguiente
      continue;
    }
    if (PERFIL_NINOS_KEYWORDS.includes(p)) {
      perfilDetectado = 'ninos';
      continue;
    }
    if (PERFIL_HOMBRE_KEYWORDS.includes(p)) {
      perfilDetectado = 'hombre';
      continue;
    }
    if (PERFIL_MUJER_KEYWORDS.includes(p)) {
      perfilDetectado = 'mujer';
      continue;
    }
    if (PERFIL_MAYORES_KEYWORDS.includes(p)) {
      perfilDetectado = 'adultos_mayores';
      continue;
    }

    if (p !== 'para' && p !== 'de' && p !== 'el' && p !== 'la') {
      palabrasRestantes.push(p);
    }
  }

  return {
    perfil: perfilDetectado,
    resto: palabrasRestantes.join(' ').trim(),
  };
};

/**
 * Filtra productos con reglas estrictas según el perfil de usuario objetivo,
 * previniendo que se cuelen productos de categorías incompatibles (ej. próstata en mujer o niños).
 */
export const filtrarPorPerfilUsuario = (productos = [], perfil) => {
  if (!perfil) return productos;

  return productos.filter(p => {
    if (p.visible === false) return false;

    const normNombre = normalizar(p.nombre || '');
    const normCat = normalizar(p.categoria || '');
    const normCats = Array.isArray(p.categorias) ? p.categorias.map(normalizar) : [];
    const normTipos = Array.isArray(p.tiposUsuario) ? p.tiposUsuario.map(normalizar) : [];

    // ── 1. PERFIL: NIÑOS ──
    if (perfil === 'ninos') {
      // Exclusión absoluta de productos de adultos / salud hormonal
      if (normCat.includes('salud masculina') || normCats.some(c => c.includes('salud masculina'))) return false;
      if (normCat.includes('bienestar femenino') || normCats.some(c => c.includes('bienestar femenino'))) return false;

      // Inclusión
      const esKidsPorTipo = normTipos.some(t => t.includes('nino') || t.includes('infantil') || t.includes('kids'));
      const esKidsPorCat = normCat.includes('nino') || normCats.some(c => c.includes('nino'));
      const esKidsPorNombre = normNombre.includes('kids') || normNombre.includes('nino') || normNombre.includes('nina') || normNombre.includes('infantil') || normNombre.includes('nutrikids');

      return esKidsPorTipo || esKidsPorCat || esKidsPorNombre;
    }

    // ── 2. PERFIL: HOMBRE ──
    if (perfil === 'hombre') {
      // Exclusión absoluta de productos exclusivamente femeninos
      if (normCat.includes('bienestar femenino') || normCats.some(c => c.includes('bienestar femenino'))) return false;
      if (normCat === 'mujeres' || normCat === 'mujer') return false;
      if (normNombre.includes('secret fem') || normNombre.includes('bbl') || normNombre.includes('aguaje')) return false;

      // Excluir si es exclusivamente de niños
      const esKidsPorTipo = normTipos.some(t => t.includes('nino') || t.includes('infantil') || t.includes('kids'));
      const tieneHombreEnTipo = normTipos.some(t => t.includes('hombre'));
      if (esKidsPorTipo && !tieneHombreEnTipo) return false;
      if (normCat === 'ninos' && !tieneHombreEnTipo) return false;

      // Excluir si es exclusivamente para mujeres (sin hombres)
      const tieneMujerEnTipo = normTipos.some(t => t.includes('mujer'));
      if (tieneMujerEnTipo && !tieneHombreEnTipo) return false;

      // Inclusión
      const esHombrePorTipo = tieneHombreEnTipo;
      const esHombrePorCat = normCat.includes('salud masculina') || normCats.some(c => c.includes('salud masculina')) || normCat.includes('hombre');
      const esHombrePorNombre = normNombre.includes('hombre') || normNombre.includes('prosta') || normNombre.includes('menforte') || normNombre.includes('papa fuerte');

      return esHombrePorTipo || esHombrePorCat || esHombrePorNombre;
    }

    // ── 3. PERFIL: MUJER ──
    if (perfil === 'mujer') {
      // Exclusión absoluta de productos de salud masculina o próstata
      if (normCat.includes('salud masculina') || normCats.some(c => c.includes('salud masculina'))) return false;
      if (normNombre.includes('prosta') || normNombre.includes('menforte') || normNombre.includes('papa fuerte')) return false;

      // Excluir si es exclusivamente de niños
      const esKidsPorTipo = normTipos.some(t => t.includes('nino') || t.includes('infantil') || t.includes('kids'));
      const tieneMujerEnTipo = normTipos.some(t => t.includes('mujer') || t.includes('mama'));
      if (esKidsPorTipo && !tieneMujerEnTipo) return false;
      if (normCat === 'ninos' && !tieneMujerEnTipo) return false;

      // Excluir si es exclusivamente para hombres (sin mujeres)
      const tieneHombreEnTipo = normTipos.some(t => t.includes('hombre'));
      if (tieneHombreEnTipo && !tieneMujerEnTipo) return false;

      // Inclusión
      const esMujerPorTipo = tieneMujerEnTipo;
      const esMujerPorCat = normCat.includes('bienestar femenino') || normCats.some(c => c.includes('bienestar femenino')) || normCat === 'mujeres' || normCat === 'mujer';
      const esMujerPorNombre = normNombre.includes('mujer') || normNombre.includes('fem') || normNombre.includes('belleza') || normNombre.includes('bbl') || normNombre.includes('aguaje') || normNombre.includes('uro collagen');

      return esMujerPorTipo || esMujerPorCat || esMujerPorNombre;
    }

    // ── 4. PERFIL: ADULTOS MAYORES ──
    if (perfil === 'adultos_mayores') {
      // Excluir productos exclusivamente de niños
      const esKidsPorTipo = normTipos.some(t => t.includes('nino') || t.includes('infantil') || t.includes('kids'));
      const tieneAdultoEnTipo = normTipos.some(t => t.includes('adulto'));
      if (esKidsPorTipo && !tieneAdultoEnTipo) return false;
      if (normCat === 'ninos') return false;

      // Inclusión: productos formulados para adultos mayores o de soporte articular/óseo/cognitivo geriátrico
      const esMayorPorTipo = normTipos.some(t => t.includes('adultos mayores'));
      const esAdultoPorTipo = normTipos.some(t => t.includes('adulto'));
      const esMayorPorNombre = normNombre.includes('glucosamina') || normNombre.includes('calcio') || normNombre.includes('ginkgo') || normNombre.includes('colageno');

      return esMayorPorTipo || (esAdultoPorTipo && esMayorPorNombre);
    }

    return true;
  });
};

/**
 * Calcula un puntaje de relevancia (score) entre un producto y la búsqueda de texto.
 */
export const puntuarProducto = (producto, queryTexto) => {
  const normQuery = normalizar(queryTexto);
  if (!normQuery) return 0;

  const normNombre = normalizar(producto.nombre || '');
  const normCat    = normalizar(producto.categoria || '');
  const normCats   = Array.isArray(producto.categorias)
    ? producto.categorias.map(normalizar).join(' ')
    : '';
  const normTipos  = Array.isArray(producto.tiposUsuario)
    ? producto.tiposUsuario.map(normalizar).join(' ')
    : '';
  const normDesc   = normalizar(producto.descripcion || '');

  // 1. Coincidencia exacta o contenida completa en el nombre
  if (normNombre === normQuery) return 100;
  if (normNombre.includes(normQuery)) return 80;

  // 2. Coincidencia en categorías o perfiles
  if (normCat === normQuery || normCats.includes(normQuery)) return 65;
  if (normTipos.includes(normQuery)) return 60;
  if (normCat.includes(normQuery)) return 50;

  // 3. Evaluar palabras expandidas y fuzzy controlada
  const terminosQuery = expandirPalabras(queryTexto);
  const palabrasProducto = `${normNombre} ${normCat} ${normCats} ${normTipos}`.split(' ').filter(Boolean);

  let puntos = 0;
  let coincidencias = 0;

  for (const qWord of terminosQuery) {
    let matchEncontrado = false;

    for (const pWord of palabrasProducto) {
      if (qWord === pWord) {
        puntos += 25;
        matchEncontrado = true;
        break;
      }
      if (pWord.startsWith(qWord) && qWord.length >= 4) {
        puntos += 18;
        matchEncontrado = true;
        break;
      }
      if (sonPalabrasSimilares(qWord, pWord)) {
        puntos += 12;
        matchEncontrado = true;
        break;
      }
    }

    if (matchEncontrado) {
      coincidencias++;
    }
  }

  // 4. Coincidencia en descripción como respaldo (solo si no es búsqueda de perfil y palabra >= 4)
  if (normQuery.length >= 4 && normDesc.includes(normQuery)) {
    puntos += 8;
  }

  if (coincidencias > 0) {
    return puntos + (coincidencias * 10);
  }

  return 0;
};

/**
 * Filtra y ordena productos usando búsqueda inteligente tolerante a errores,
 * sinónimos y con estricta segmentación por tipo de usuario (Niños, Hombre, Mujer, Adultos Mayores).
 */
export const buscarProductosInteligente = (todosLosProductos = [], queryTexto = '') => {
  if (!queryTexto || !queryTexto.trim()) return [];

  // 1. Verificar si es una búsqueda directa por perfil de usuario (ej. clic en chip o escribir "niños", "hombre", "mujer")
  const perfilPuro = detectarPerfilUsuario(queryTexto);
  if (perfilPuro) {
    return filtrarPorPerfilUsuario(todosLosProductos, perfilPuro);
  }

  // 2. Verificar si es una búsqueda combinada (ej. "colageno mujer", "gomitas ninos", "proteina hombre")
  const { perfil, resto } = extraerPerfilYTermino(queryTexto);
  if (perfil && resto) {
    const productosPerfil = filtrarPorPerfilUsuario(todosLosProductos, perfil);
    const evaluados = [];
    for (const prod of productosPerfil) {
      const score = puntuarProducto(prod, resto);
      if (score > 0) {
        evaluados.push({ producto: prod, score });
      }
    }
    evaluados.sort((a, b) => b.score - a.score);
    return evaluados.map(item => item.producto);
  }

  // 3. Búsqueda de texto libre general
  const evaluados = [];
  for (const prod of todosLosProductos) {
    if (prod.visible === false) continue;
    const score = puntuarProducto(prod, queryTexto);
    if (score > 0) {
      evaluados.push({ producto: prod, score });
    }
  }

  evaluados.sort((a, b) => b.score - a.score);
  return evaluados.map(item => item.producto);
};

/**
 * Obtiene sugerencias en tiempo real para el autocompletado del buscador.
 */
export const obtenerSugerenciasEnVivo = (todosLosProductos = [], queryTexto = '', max = 5) => {
  const norm = normalizar(queryTexto);
  if (!norm || norm.length < 2) return [];

  const productosFiltrados = buscarProductosInteligente(todosLosProductos, queryTexto);
  return productosFiltrados.slice(0, max);
};
